export const extractAndSortPoints = (vertices) => {
  return vertices
    .flatMap((vertex) => vertex.points)
    .sort((a, b) => {
      if (a.x === b.x) {
        return a.y - b.y;
      }
      return a.x - b.x;
    });
};

const calculateDistance = (point1, point2) => {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

 export const normalizeVerticesForMeasurementLine = (vertices, width, height) => {
      return vertices.map(vertex => ({
        points: vertex.points.map(point => ({
          x: point.x / width,
          y: point.y / height
        }))
      }));
};

export const isValidNumber = (val) => typeof val === 'number' && !isNaN(val);


export const findClosestLineToDimension = (vertices, width, height) => {
  if (!vertices || vertices.length === 0) return -1;

  const useFallback = width == 0 || height == 0;

  if (useFallback) {
    const lengths = vertices
    .map((vertex, originalIndex) => ({
      index: originalIndex,
      length: calculateDistance(vertex.points[0], vertex.points[1]),
    }))
    .slice(0, 4); 
    
    lengths.sort((a, b) => a.length - b.length);
    const midIndex = Math.floor(lengths.length / 2);
    return midIndex + 1 < lengths.length
      ? lengths[midIndex + 1].index
      : lengths[midIndex].index;
  }

  let closest = null;

  vertices.forEach((vertex, index) => {
    const [p1, p2] = vertex.points;
    const length = calculateDistance(p1, p2);

    const widthDiff = Math.abs(length - width);
    const heightDiff = Math.abs(length - height);

    const minDiff = Math.min(widthDiff, heightDiff);

    if (!closest || minDiff < closest.diff) {
      closest = { index, diff: minDiff, length };
    }
  });

  return closest ? closest.index : -1;
};

export function isPointInsidePolygon(x, y, walls) {
  let inside = false;
  for (let i = 0, j = walls.length - 1; i < walls.length; j = i++) {
    const xi = walls[i].start.x,
      yi = walls[i].start.y;
    const xj = walls[j].start.x,
      yj = walls[j].start.y;

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function getBoxesInsideWalls(boxes, walls) {
  if (boxes.length === 0) return walls; 

  const boxesInside = boxes?.filter((box) => {
    const boxVertices = [
      { x: box.left, y: box.top }, 
      { x: box.left + box.width, y: box.top }, 
      { x: box.left, y: box.top + box.height }, 
      { x: box.left + box.width, y: box.top + box.height }, 
    ];

    return boxVertices.every((vertex) =>
      isPointInsidePolygon(vertex.x, vertex.y, walls)
    );
  });

  return boxesInside;
}

export function getMaxWidthBox(boxes) {
  if (boxes?.length === 0) return null; 

  const maxWidthBox = boxes?.reduce((maxBox, currentBox) => {
    return currentBox.width > maxBox.width ? currentBox : maxBox;
  });

  return maxWidthBox;
}

export function getClosestWalls(box, walls) {
  const boxCorners = [
    { x: box.left, y: box.top }, 
    { x: box.left + box.width, y: box.top }, 
    { x: box.left, y: box.top + box.height }, 
    { x: box.left + box.width, y: box.top + box.height }, 
  ];

  function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;
    if (len_sq !== 0) param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 0) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  const wallsWithDistance = walls.map((wall) => {
    const minDistance = Math.min(
      ...boxCorners.map((corner) =>
        pointToSegmentDistance(
          corner.x,
          corner.y,
          wall.start.x,
          wall.start.y,
          wall.end.x,
          wall.end.y
        )
      )
    );
    return { ...wall, distance: minDistance };
  });

  wallsWithDistance.sort((a, b) => a.distance - b.distance);

  return wallsWithDistance.slice(0, wallsWithDistance.length);
}
